/** Known ASR Devanagari spellings of English clinical words (do not map everything to tooth). */
const DEVANAGARI_CLINICAL: Record<string, string> = {
  टू: 'tooth',
  तोथ: 'tooth',
  टुथ: 'tooth',
  टूथ: 'tooth',
  तुथ: 'tooth',
  तोथ्: 'tooth',
  इम्प्लांट: 'implant',
  इम्प्लान्ट: 'implant',
  इम्प्लाण्ट: 'implant',
  फिलिंग: 'filling',
  फिलिङ: 'filling',
  क्राउन: 'crown',
  कैरीज: 'caries',
  कैरीज़: 'caries',
  कंपोजिट: 'composite',
  कम्पोजिट: 'composite',
  मेसियल: 'mesial',
  डिस्टल: 'distal',
  बक्कल: 'buccal',
  ओक्लूसल: 'occlusal',
  अनडू: 'undo',
  अंडू: 'undo',
}

const INDEPENDENT_VOWELS: Record<string, string> = {
  अ: 'a',
  आ: 'a',
  इ: 'i',
  ई: 'i',
  उ: 'u',
  ऊ: 'u',
  ए: 'e',
  ऐ: 'ai',
  ओ: 'o',
  औ: 'au',
  ऋ: 'ri',
}

const CONSONANTS: Record<string, string> = {
  क: 'k',
  ख: 'kh',
  ग: 'g',
  घ: 'gh',
  ङ: 'ng',
  च: 'ch',
  छ: 'chh',
  ज: 'j',
  झ: 'jh',
  ञ: 'ny',
  ट: 't',
  ठ: 'th',
  ड: 'd',
  ढ: 'dh',
  ण: 'n',
  त: 't',
  थ: 'th',
  द: 'd',
  ध: 'dh',
  न: 'n',
  प: 'p',
  फ: 'f',
  ब: 'b',
  भ: 'bh',
  म: 'm',
  य: 'y',
  र: 'r',
  ल: 'l',
  व: 'v',
  श: 'sh',
  ष: 'sh',
  स: 's',
  ह: 'h',
  ळ: 'l',
}

const MATRAS: Record<string, string> = {
  'ा': 'a',
  'ि': 'i',
  'ी': 'i',
  'ु': 'u',
  'ू': 'u',
  'ृ': 'ri',
  'े': 'e',
  'ै': 'ai',
  'ो': 'o',
  'ौ': 'au',
}

const VIRAMA = '\u094D'
const ANUSVARA = '\u0902'
const CANDRABINDU = '\u0901'
const VISARGA = '\u0903'
const DEVANAGARI_RUN = /[\u0900-\u097F]+/g

/** Approximate Latin for English loanwords written in Devanagari (e.g. इम्प्लांट → implant). */
const transliterateDevanagariRun = (run: string): string => {
  let out = ''
  let index = 0
  while (index < run.length) {
    const ch = run[index]
    if (INDEPENDENT_VOWELS[ch]) {
      out += INDEPENDENT_VOWELS[ch]
      index++
      continue
    }
    if (ch === ANUSVARA || ch === CANDRABINDU) {
      out += 'n'
      index++
      continue
    }
    if (ch === VISARGA) {
      out += 'h'
      index++
      continue
    }
    const consonant = CONSONANTS[ch]
    if (consonant) {
      out += consonant
      index++
      if (index < run.length && run[index] === VIRAMA) {
        index++
        continue
      }
      if (index < run.length && MATRAS[run[index]]) {
        out += MATRAS[run[index]]
        index++
        continue
      }
      // English loanwords usually drop final inherent schwa.
      if (index < run.length && CONSONANTS[run[index]]) out += 'a'
      continue
    }
    if (MATRAS[ch]) {
      out += MATRAS[ch]
      index++
      continue
    }
    if (ch === VIRAMA) {
      index++
      continue
    }
    index++
  }
  return out
}

const mapDevanagariRun = (run: string): string =>
  DEVANAGARI_CLINICAL[run] ?? transliterateDevanagariRun(run)

/** Map Devanagari ASR tokens to Latin clinical English; never blanket-replace with tooth. */
export const latinizeDevanagariClinical = (text: string): string =>
  text.replace(DEVANAGARI_RUN, mapDevanagariRun)
